import { CheckCircle, WarningCircle } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { LoadingState } from '@/components/loading-state'
import { ErrorState } from '@/components/error-state'

import { authApi } from '../api'
import { AuthLayout } from '../components/auth-layout'
import { Notice } from '../components/notice'
import {
  isEmailConflictError,
  isInvalidEmailTokenError,
} from '../error-keys'
import { verificationWindowMinutes } from '../verification'

type VerifyStatus = 'verifying' | 'success' | 'invalid' | 'conflict' | 'error'

const inFlightVerifications = new Map<string, Promise<void>>()
const verifiedTokens = new Set<string>()

/**
 * Deduplicates the email verification request across React StrictMode's
 * double effect invocation. Email tokens are single-use, so issuing the same
 * request twice could turn a success into an "invalid link" error.
 */
function verifyOnce(token: string): Promise<void> {
  if (verifiedTokens.has(token)) return Promise.resolve()
  const existing = inFlightVerifications.get(token)
  if (existing) return existing

  const request = authApi
    .verifyEmail(token)
    .then(() => {
      verifiedTokens.add(token)
    })
    .finally(() => {
      inFlightVerifications.delete(token)
    })
  inFlightVerifications.set(token, request)
  return request
}

/**
 * Email verification landing (`/verify-email?token=`). Runs the action
 * automatically and distinguishes an expired/used token (401, D-049) from a
 * race conflict (409) and a transient network failure. An expired link lasting
 * 15 minutes means the user must register again (UI_PARITY §3.4).
 */
export function VerifyEmailScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')?.trim() ?? ''
  const minutes = verificationWindowMinutes()

  const [status, setStatus] = useState<VerifyStatus>(
    token ? 'verifying' : 'invalid',
  )
  const [attempt, setAttempt] = useState(0)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (!token) return

    let cancelled = false
    void verifyOnce(token)
      .then(() => {
        if (!cancelled) setStatus('success')
      })
      .catch((reason: unknown) => {
        if (cancelled) return
        setError(reason)
        setStatus(
          isInvalidEmailTokenError(reason)
            ? 'invalid'
            : isEmailConflictError(reason)
              ? 'conflict'
              : 'error',
        )
      })

    return () => {
      cancelled = true
    }
  }, [token, attempt])

  function retry() {
    setStatus('verifying')
    setError(null)
    setAttempt((value) => value + 1)
  }

  function goToLogin() {
    navigate('/login?setupProfile=1', { replace: true })
  }

  return (
    <AuthLayout
      title={t('screens.verifyEmail.title')}
      maxWidth={520}
      backTo="/login"
    >
      {status === 'verifying' ? (
        <LoadingState labelKey="auth.verifyEmail.checking" />
      ) : null}

      {status === 'success' ? (
        <div className="flex flex-col items-center text-center">
          <CheckCircle
            size={64}
            weight="bold"
            aria-hidden="true"
            className="text-teal"
          />
          <Notice tone="success" className="mt-5 w-full">
            {t('auth.verifyEmail.success')}
          </Notice>
          <button
            type="button"
            className="btn-primary mt-6 w-full"
            onClick={goToLogin}
          >
            {t('auth.verifyEmail.goToLogin')}
          </button>
        </div>
      ) : null}

      {status === 'invalid' ? (
        <div className="flex flex-col items-center text-center">
          <WarningCircle
            size={64}
            weight="bold"
            aria-hidden="true"
            className="text-danger"
          />
          <Notice tone="error" className="mt-5 w-full">
            {t('auth.verifyEmail.invalidToken')}
          </Notice>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            {t('auth.verifyEmail.linkLifetime', { count: minutes })}
          </p>
          <Link to="/register" className="btn-primary mt-6 w-full">
            {t('auth.verifyEmail.registerAgain')}
          </Link>
          <button
            type="button"
            className="btn-ghost mt-1 w-full"
            onClick={goToLogin}
          >
            {t('auth.verifyEmail.goToLogin')}
          </button>
        </div>
      ) : null}

      {status === 'conflict' ? (
        <div className="flex flex-col items-center text-center">
          <WarningCircle
            size={64}
            weight="bold"
            aria-hidden="true"
            className="text-danger"
          />
          <Notice tone="error" className="mt-5 w-full">
            {t('auth.verifyEmail.conflict')}
          </Notice>
          <button
            type="button"
            className="btn-primary mt-6 w-full"
            onClick={goToLogin}
          >
            {t('auth.verifyEmail.goToLogin')}
          </button>
          <Link to="/register" className="btn-ghost mt-1 w-full">
            {t('auth.verifyEmail.registerAgain')}
          </Link>
        </div>
      ) : null}

      {status === 'error' ? (
        <div className="space-y-4">
          <ErrorState
            error={error}
            titleKey="auth.verifyEmail.errorTitle"
            onRetry={retry}
          />
          <button
            type="button"
            className="btn-ghost w-full"
            onClick={goToLogin}
          >
            {t('auth.verifyEmail.goToLogin')}
          </button>
        </div>
      ) : null}
    </AuthLayout>
  )
}
